import React, { useContext, useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import { FiMenu, FiX, FiMessageCircle } from 'react-icons/fi';
import { Capacitor } from '@capacitor/core';
import { useMessages } from '../Messages/MessagesContext';
import { useNotifications } from '../Notifications/NotificationsContext';
import { sessionIsAdmin } from '../../utils/adminAuth';
import { prefetchHandlers } from '../../utils/prefetchRoute';
import { scrollToHomePageHash } from '../../utils/homePageSectionScroll';
import './Header.css';

const Header = () => {
    const { user, logout } = useContext(UserContext);
    const { unreadTotal, toggleMessages } = useMessages();
    const { unreadCount: profileUnreadCount } = useNotifications();
    const navigate = useNavigate();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false); // State for menu toggle
    const [isAndroidPhone, setIsAndroidPhone] = useState(false); // State for Android phone detection
    const [isScrolled, setIsScrolled] = useState(() => typeof window !== 'undefined' && window.scrollY > 0);
    const headerRef = useRef(null);
    const isAdminSession = sessionIsAdmin(user);
    const hideCollectorChrome = location.pathname.startsWith('/admin') || isAdminSession;

    const handleLogout = () => {
        logout();
        navigate(isAdminSession ? '/login' : '/');
        setMenuOpen(false); // Close menu after logout
    };

    const handleMessagesClick = () => {
        setMenuOpen(false);
        if (!user) {
            navigate('/login', {
                state: {
                    redirectTo: location.pathname + location.search,
                    message: 'Please log in to view your messages.',
                },
            });
            return;
        }
        toggleMessages();
    };

    const messagesButton = (className) => (
        <button
            type="button"
            className={className}
            onClick={handleMessagesClick}
            aria-label="Messages"
        >
            <FiMessageCircle />
            {unreadTotal > 0 && (
                <span className="header-unread-badge">{unreadTotal > 99 ? '99+' : unreadTotal}</span>
            )}
        </button>
    );

    const handleHomeClick = () => {
        setMenuOpen(false);
        // Navigate to homepage and scroll to top
        navigate('/');
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
    };

    const handleSignUpClick = (event) => {
        event.preventDefault();
        setMenuOpen(false);
        const targetHash = '#HomePage-community-section';
        if (location.pathname === '/' && location.hash === targetHash) {
            scrollToHomePageHash(targetHash);
            return;
        }
        navigate('/#HomePage-community-section', { state: { user } });
    };

    // Detect Android phone and apply appropriate styling
    useEffect(() => {
        const detectAndroidPhone = async () => {
            const isAndroid = Capacitor.getPlatform() === 'android';
            const isPhone = window.innerWidth <= 767;
            
            if (isAndroid && isPhone) {
                try {
                    // Get the actual status bar height from the device
                    const { StatusBar } = await import('@capacitor/status-bar');
                    const statusBarInfo = await StatusBar.getInfo();
                    const statusBarHeight = statusBarInfo.height || 24; // Default to 24px if not available
                    
                    // Set dynamic CSS variable for status bar height
                    document.documentElement.style.setProperty('--status-bar-height', `${statusBarHeight}px`);
                    setIsAndroidPhone(true);
                } catch (error) {
                    console.log('Could not get status bar height, using default:', error);
                    document.documentElement.style.setProperty('--status-bar-height', '24px');
                    setIsAndroidPhone(true);
                }
            } else {
                setIsAndroidPhone(false);
            }
        };

        detectAndroidPhone();
        window.addEventListener('resize', detectAndroidPhone);
        
        return () => window.removeEventListener('resize', detectAndroidPhone);
    }, []);

    useEffect(() => {
        const el = headerRef.current;
        if (!el || hideCollectorChrome) return undefined;

        const publishOffset = () => {
            const h = Math.ceil(el.getBoundingClientRect().height);
            if (h > 0) {
                document.documentElement.style.setProperty('--msv-header-offset', `${h}px`);
            }
        };

        publishOffset();
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publishOffset) : null;
        ro?.observe(el);
        window.addEventListener('resize', publishOffset);

        return () => {
            ro?.disconnect();
            window.removeEventListener('resize', publishOffset);
        };
    }, [hideCollectorChrome, isAndroidPhone, menuOpen]);

    useEffect(() => {
        const setScrolled = (scrolled) => {
            headerRef.current?.classList.toggle('scrolled', scrolled);
            setIsScrolled((prev) => (prev === scrolled ? prev : scrolled));
        };

        const updateFromScrollPosition = () => {
            setScrolled((window.scrollY || document.documentElement.scrollTop || 0) > 0);
        };

        const handleWheel = (event) => {
            if (event.deltaY > 0) {
                setScrolled(true);
                return;
            }
            updateFromScrollPosition();
        };

        const handleTouchMove = () => {
            requestAnimationFrame(updateFromScrollPosition);
        };

        updateFromScrollPosition();
        window.addEventListener('scroll', updateFromScrollPosition, { passive: true });
        window.addEventListener('wheel', handleWheel, { passive: true });
        window.addEventListener('touchmove', handleTouchMove, { passive: true });

        return () => {
            window.removeEventListener('scroll', updateFromScrollPosition);
            window.removeEventListener('wheel', handleWheel);
            window.removeEventListener('touchmove', handleTouchMove);
        };
    }, []);

    // Close mobile menu when tapping/clicking outside the header
    useEffect(() => {
        if (!menuOpen) return undefined;

        const handlePointerOutside = (event) => {
            const header = headerRef.current;
            if (!header) return;
            if (header.contains(event.target)) return;
            setMenuOpen(false);
        };

        document.addEventListener('pointerdown', handlePointerOutside);
        return () => document.removeEventListener('pointerdown', handlePointerOutside);
    }, [menuOpen]);

    // Close menu on route change
    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    // Admin portal has its own chrome; keep the collector header off those routes / sessions.
    if (hideCollectorChrome) {
        return null;
    }

    return (
        <header ref={headerRef} className={`menu-bar ${isAndroidPhone ? 'android-phone' : ''} ${isScrolled ? 'scrolled' : ''}`}>
             <div className="menu-bar-content">
            <Link to="/" className="logo-link" onClick={handleHomeClick}>
                <img
                    src={require('./msvLogoWhite.png')}
                    alt="Logo"
                    className="navbar-logo"
                />
            </Link>
            <div className="header-mobile-actions">
                {messagesButton('header-messages-btn mobile-only')}
                <button
                    type="button"
                    className="menu-toggle"
                    onClick={() => setMenuOpen(!menuOpen)}
                    aria-label={
                        profileUnreadCount > 0
                            ? `Menu, ${profileUnreadCount > 99 ? '99+' : profileUnreadCount} unread notifications`
                            : 'Menu'
                    }
                >
                    {menuOpen ? <FiX /> : <FiMenu />}
                    {profileUnreadCount > 0 ? (
                        <span className="header-unread-badge">
                            {profileUnreadCount > 99 ? '99+' : profileUnreadCount}
                        </span>
                    ) : null}
                </button>
            </div>
            </div>

            <div className={`menu-links ${menuOpen ? 'open' : ''}`}>
                <div className="menu-links-nav">
                    <Link to="/" className="menu-link" onClick={handleHomeClick}>Home</Link>

                    <Link
                        to="/searchcollectibles"
                        className="menu-link"
                        onClick={() => setMenuOpen(false)}
                    >
                        <span style={{ fontSize: '1.2rem' }}>THE VAULT</span>
                    </Link>

                    <Link
                        to="/marketplace"
                        className="menu-link"
                        onClick={() => setMenuOpen(false)}
                    >
                        <span style={{ fontSize: '1.2rem' }}>Marketplace</span>
                    </Link>

                    <Link
                        to="/nascardFantasy"
                        className="menu-link"
                        {...prefetchHandlers('/nascardFantasy')}
                        onClick={() => setMenuOpen(false)}
                    >
                        <span style={{ fontSize: '1.2rem' }}>NASCard Fantasy</span>
                    </Link>

                    <Link to="/auctions" className="menu-link" onClick={() => setMenuOpen(false)}>
                        Auctions
                    </Link>
                </div>

                <div className="menu-links-account">
                    {messagesButton('header-messages-btn desktop-only')}
                    {user ? (
                        <>
                            <Link
                                to="/profilepage"
                                className="menu-link menu-link--profile"
                                state={{ user }}
                                onClick={() => setMenuOpen(false)}
                            >
                                My Profile
                                {profileUnreadCount > 0 ? (
                                    <span className="header-unread-badge header-unread-badge--profile">
                                        {profileUnreadCount > 99 ? '99+' : profileUnreadCount}
                                    </span>
                                ) : null}
                            </Link>
                            <button className="menu-btn logout-btn" onClick={handleLogout} style={{fontFamily: 'inherit'}}>Logout</button>
                        </>
                    ) : (
                        <>
                            <Link to="/#HomePage-community-section" className="menu-link" state={{ user }} onClick={handleSignUpClick}>Sign Up</Link>
                            <Link to="/login" className="menu-btn logout-btn" state={{ user }} onClick={() => setMenuOpen(false)}>Log In</Link>
                        </>
                    )}
                </div>
            </div>
        </header>

    );
};

export default Header;
